// src/utils/moveValidator.ts
import {
  Position,
  Board,
  Piece,
  PieceColor,
  MovementPattern,
} from "../types/Board";
import {
  isValidPosition,
  getPieceAt,
  isPathClear,
  positionsEqual,
  findKing,
  cloneBoard,
  setPieceAt,
} from "./boardUtils.ts";

export interface MoveValidationResult {
  isValid: boolean;
  reason?: string;
  capturedPiece?: Piece;
  isCastling?: boolean;
  castlingRookFrom?: Position;
  castlingRookTo?: Position;
}

export class MoveValidator {
  /**
   * Get valid moves for PlayOnline (using Map structure)
   * This is an adapter method that converts Map to Board structure
   */
  getValidMoves(
    position: Position,
    piecesMap: Map<string, Piece>,
    rows: number,
    cols: number,
    currentTurn: "white" | "black",
    moveHistory: any[]
  ): Position[] {
    // PlayOnline uses "row-col" format, try both formats
    let key = `${position.row}-${position.col}`;
    let piece = piecesMap.get(key);

    // If not found, try comma format
    if (!piece) {
      key = `${position.row},${position.col}`;
      piece = piecesMap.get(key);
    }

    if (!piece) {
      return [];
    }

    // Convert Map to Board structure
    const board = this.mapToBoard(piecesMap, rows, cols);

    // Use the standard getLegalMoves method
    return this.getLegalMoves(piece, board);
  }

  /**
   * PUBLIC method to check if king is in check (for PlayOnline)
   * This converts the Map structure to Board and checks
   */
  isKingInCheck(
    piecesMap: Map<string, Piece>,
    color: "white" | "black",
    rows: number,
    cols: number
  ): boolean {
    // Validate inputs
    if (!piecesMap || !(piecesMap instanceof Map)) {
      console.error('isKingInCheck: piecesMap is not a valid Map');
      return false;
    }
    
    if (!rows || !cols || rows <= 0 || cols <= 0) {
      console.error('isKingInCheck: Invalid board dimensions', { rows, cols });
      return false;
    }
    
    // Convert Map to Board structure
    const board = this.mapToBoard(piecesMap, rows, cols);
    
    // Validate board was created properly
    if (!board || !board.dimensions || !board.squares) {
      console.error('isKingInCheck: Failed to create valid board structure');
      return false;
    }
    
    // Use the private method to check
    return this.isKingInCheckInternal(board, color);
  }

  /**
   * Convert a Map of pieces to a Board structure
   * CRITICAL: Ensures all pieces have position property set
   */
  private mapToBoard(
    piecesMap: Map<string, Piece>,
    rows: number,
    cols: number
  ): Board {
    // Initialize empty board
    const squares: (Piece | null)[][] = [];
    for (let row = 0; row < rows; row++) {
      squares[row] = [];
      for (let col = 0; col < cols; col++) {
        squares[row][col] = null;
      }
    }

    // Place pieces on the board
    // Handle both "row-col" and "row,col" key formats
    piecesMap.forEach((piece, key) => {
      // Try splitting by hyphen first (PlayOnline format)
      let parts = key.split("-");
      if (parts.length !== 2) {
        // Try splitting by comma (alternative format)
        parts = key.split(",");
      }

      if (parts.length === 2) {
        const row = parseInt(parts[0], 10);
        const col = parseInt(parts[1], 10);

        if (!isNaN(row) && !isNaN(col) && row >= 0 && row < rows && col >= 0 && col < cols) {
          // CRITICAL: Ensure piece has position property
          // This is essential for findKing and other utilities to work
          const pieceWithPosition: Piece = piece.position 
            ? piece 
            : { ...piece, position: { row, col } };
          
          squares[row][col] = pieceWithPosition;
        }
      }
    });

    return {
      dimensions: { rows, cols },
      squares,
    };
  }

  /**
   * Get all legal moves for a piece, considering check and game rules
   * @param diagonalCaptureOnly - If true, Knave/Squire can only capture diagonally (default: false = can capture forward too)
   */
  getLegalMoves(piece: Piece, board: Board, diagonalCaptureOnly: boolean = false): Position[] {
    const pseudoLegalMoves = this.getPseudoLegalMoves(piece, board, diagonalCaptureOnly);

    // Filter out moves that would leave king in check
    return pseudoLegalMoves.filter((to) => {
      const testBoard = cloneBoard(board);

      // Handle castling moves specially
      if (this.isCastlingMove(piece, piece.position, to, board)) {
        const rookPositions = this.getCastlingRookPositions(
          piece.position,
          to,
          board
        );
        if (rookPositions) {
          // Move the king
          this.applyMove(testBoard, piece.position, to);
          // Move the rook
          const rook = getPieceAt(testBoard, rookPositions.rookFrom);
          if (rook) {
            setPieceAt(testBoard, rookPositions.rookFrom, null);
            setPieceAt(testBoard, rookPositions.rookTo, {
              ...rook,
              position: rookPositions.rookTo,
              hasMoved: true,
            });
          }
        }
      } else {
        this.applyMove(testBoard, piece.position, to);
      }

      return !this.isKingInCheckInternal(testBoard, piece.color);
    });
  }

  /**
   * Get pseudo-legal moves (without considering check)
   * @param diagonalCaptureOnly - If true, Knave/Squire can only capture diagonally
   */
  private getPseudoLegalMoves(
    piece: Piece,
    board: Board,
    diagonalCaptureOnly: boolean = false,
    includeCastling: boolean = true
  ): Position[] {
    const moves: Position[] = [];

    // Handle special pawn-like pieces (forward only)
    if (this.isPawnLikePiece(piece.type)) {
      moves.push(...this.getPawnMoves(piece, board, diagonalCaptureOnly));
      return moves;
    }

    // Handle standard piece movements
    for (const pattern of piece.rules.movements) {
      moves.push(...this.getMovesForPattern(piece, pattern, board));
    }

    // Add Slide Bishop moves for bishop, priest, and nun on 9x9 and 11x11 boards
    // These pieces gain the ability to slide 1 square horizontally (left or right)
    const boardSize = board.dimensions.cols;
    const pieceType = piece.type.toLowerCase();
    if ((boardSize === 9 || boardSize === 11) &&
        (pieceType === "bishop" || pieceType === "priest" || pieceType === "nun")) {
      moves.push(...this.getSlideBishopMoves(piece, board));
    }

    // Add castling moves for King (not Emperor) - but only when not checking for attacks
    if (includeCastling && piece.type.toLowerCase() === "king") {
      moves.push(...this.getCastlingMoves(piece, board));
    }

    return moves;
  }

  /**
   * Get slide bishop moves (1 square horizontal slide for bishop, priest, nun)
   * Only available on 9x9 and 11x11 boards
   */
  private getSlideBishopMoves(piece: Piece, board: Board): Position[] {
    const moves: Position[] = [];
    const { row, col } = piece.position;

    // Check left slide
    const leftPos: Position = { row, col: col - 1 };
    if (isValidPosition(leftPos, board)) {
      const targetPiece = getPieceAt(board, leftPos);
      if (!targetPiece || targetPiece.color !== piece.color) {
        moves.push(leftPos);
      }
    }

    // Check right slide
    const rightPos: Position = { row, col: col + 1 };
    if (isValidPosition(rightPos, board)) {
      const targetPiece = getPieceAt(board, rightPos);
      if (!targetPiece || targetPiece.color !== piece.color) {
        moves.push(rightPos);
      }
    }

    return moves;
  }

  /**
   * Check if piece is pawn-like (forward only movement)
   */
  private isPawnLikePiece(type: string): boolean {
    return ["pawn", "knave", "squire"].includes(type.toLowerCase());
  }

  /**
   * Get castling moves for the King
   */
  private getCastlingMoves(king: Piece, board: Board): Position[] {
    const moves: Position[] = [];
    const boardSize = board.dimensions.cols;

    // Only allow castling on 8x8 and 9x9 boards
    if (boardSize !== 8 && boardSize !== 9) {
      return moves;
    }

    // King must not have moved
    if (king.hasMoved) {
      return moves;
    }

    // NOTE: We don't check if king is in check here to avoid infinite recursion
    // The check will be done when filtering legal moves in getLegalMoves()

    const kingRow = king.position.row;
    const kingCol = king.position.col;

    // Check kingside castling (right side)
    const kingsideRookCol = boardSize - 1;
    const kingsideRook = getPieceAt(board, {
      row: kingRow,
      col: kingsideRookCol,
    });

    if (
      kingsideRook &&
      (kingsideRook.type.toLowerCase() === "castle" ||
        kingsideRook.type.toLowerCase() === "rook") &&
      kingsideRook.color === king.color &&
      !kingsideRook.hasMoved
    ) {
      // Check if squares between king and rook are empty
      let pathClear = true;
      for (let col = kingCol + 1; col < kingsideRookCol; col++) {
        if (getPieceAt(board, { row: kingRow, col })) {
          pathClear = false;
          break;
        }
      }

      if (pathClear) {
        // NOTE: We don't check if squares are under attack here to avoid recursion
        // This will be validated in getLegalMoves() which filters out moves leaving king in check
        moves.push({ row: kingRow, col: kingCol + 2 });
      }
    }

    // Check queenside castling (left side)
    const queensideRookCol = 0;
    const queensideRook = getPieceAt(board, {
      row: kingRow,
      col: queensideRookCol,
    });

    if (
      queensideRook &&
      (queensideRook.type.toLowerCase() === "castle" ||
        queensideRook.type.toLowerCase() === "rook") &&
      queensideRook.color === king.color &&
      !queensideRook.hasMoved
    ) {
      // Check if squares between king and rook are empty
      let pathClear = true;
      for (let col = queensideRookCol + 1; col < kingCol; col++) {
        if (getPieceAt(board, { row: kingRow, col })) {
          pathClear = false;
          break;
        }
      }

      if (pathClear) {
        // NOTE: We don't check if squares are under attack here to avoid recursion
        // This will be validated in getLegalMoves() which filters out moves leaving king in check
        moves.push({ row: kingRow, col: kingCol - 2 });
      }
    }

    return moves;
  }

  /**
   * Check if a move is a castling move
   */
  isCastlingMove(
    king: Piece,
    from: Position,
    to: Position,
    board: Board
  ): boolean {
    if (king.type.toLowerCase() !== "king") return false;
    if (king.hasMoved) return false;

    const colDiff = Math.abs(to.col - from.col);
    return colDiff === 2;
  }

  /**
   * Get rook positions for castling
   */
  getCastlingRookPositions(
    kingFrom: Position,
    kingTo: Position,
    board: Board
  ): { rookFrom: Position; rookTo: Position } | null {
    const boardSize = board.dimensions.cols;
    const kingRow = kingFrom.row;

    if (kingTo.col > kingFrom.col) {
      // Kingside castling
      return {
        rookFrom: { row: kingRow, col: boardSize - 1 },
        rookTo: { row: kingRow, col: kingTo.col - 1 },
      };
    } else {
      // Queenside castling
      return {
        rookFrom: { row: kingRow, col: 0 },
        rookTo: { row: kingRow, col: kingTo.col + 1 },
      };
    }
  }

  /**
   * Get pawn moves (forward only pieces)
   * @param diagonalCaptureOnly - If true, Knave/Squire can only capture diagonally (default behavior)
   *                              If false, Knave/Squire can also capture forward
   */
  private getPawnMoves(piece: Piece, board: Board, diagonalCaptureOnly: boolean = false): Position[] {
    const moves: Position[] = [];
    const direction = piece.color === "white" ? -1 : 1;
    const { row, col } = piece.position;

    // Determine max forward moves based on piece type
    let maxForward = 1;
    let maxDiagonal = 1;
    let openingMaxForward = 1;

    // Check if this piece can capture forward (Knave/Squire when diagonalCaptureOnly is false)
    const canCaptureForward = !diagonalCaptureOnly &&
      (piece.type === "knave" || piece.type === "squire");

    if (piece.type === "pawn") {
      maxForward = 1;
      maxDiagonal = 1;
      openingMaxForward = 2;
    } else if (piece.type === "knave") {
      maxForward = 2;
      maxDiagonal = 2;
      openingMaxForward = 2;
    } else if (piece.type === "squire") {
      maxForward = 3;
      maxDiagonal = 3;
      openingMaxForward = 3;
    }

    // Use opening max if piece hasn't moved
    const actualMaxForward = !piece.hasMoved ? openingMaxForward : maxForward;

    // Forward moves - can capture if canCaptureForward is true
    for (let distance = 1; distance <= actualMaxForward; distance++) {
      const targetPos: Position = { row: row + direction * distance, col };

      if (!isValidPosition(targetPos, board)) break;

      const targetPiece = getPieceAt(board, targetPos);

      if (targetPiece) {
        // If we can capture forward and it's an enemy piece, add as capture move
        if (canCaptureForward && targetPiece.color !== piece.color) {
          moves.push(targetPos);
        }
        // Either way, we're blocked from moving further forward
        break;
      }

      // Check path is clear for multi-square moves
      if (distance > 1) {
        const pathPos: Position = { row: row + direction, col };
        if (getPieceAt(board, pathPos)) break;
      }

      moves.push(targetPos);
    }

    // Diagonal moves/captures - track blocked directions
    const blockedDiagonals = new Set<number>();

    for (let distance = 1; distance <= maxDiagonal; distance++) {
      for (const colDelta of [-1, 1]) {
        // Skip if this direction is already blocked
        if (blockedDiagonals.has(colDelta)) continue;

        const targetPos: Position = {
          row: row + direction * distance,
          col: col + colDelta * distance,
        };

        if (!isValidPosition(targetPos, board)) {
          blockedDiagonals.add(colDelta);
          continue;
        }

        const targetPiece = getPieceAt(board, targetPos);

        // For pawn: only capture diagonally
        if (piece.type === "pawn") {
          if (targetPiece && targetPiece.color !== piece.color) {
            moves.push(targetPos);
          }
          // Pawns don't slide diagonally, so block after first check
          blockedDiagonals.add(colDelta);
        }
        // For knave and squire: can move or capture diagonally (sliding)
        else {
          if (!targetPiece) {
            // Empty square - can move here
            moves.push(targetPos);
          } else if (targetPiece.color !== piece.color) {
            // Enemy piece - can capture, then block this direction
            moves.push(targetPos);
            blockedDiagonals.add(colDelta);
          } else {
            // Friendly piece - block this direction
            blockedDiagonals.add(colDelta);
          }
        }
      }
    }

    return moves;
  }

  /**
   * Get moves for a specific movement pattern
   */
  private getMovesForPattern(
    piece: Piece,
    pattern: MovementPattern,
    board: Board
  ): Position[] {
    const moves: Position[] = [];

    for (const direction of pattern.directions) {
      moves.push(...this.getMovesInDirection(piece, direction, pattern, board));
    }

    return moves;
  }

  /**
   * Get moves in a specific direction
   */
  private getMovesInDirection(
    piece: Piece,
    direction: { rowDelta: number; colDelta: number },
    pattern: MovementPattern,
    board: Board
  ): Position[] {
    const moves: Position[] = [];
    const maxDistance =
      pattern.maxDistance === "unlimited"
        ? Math.max(board.dimensions.rows, board.dimensions.cols)
        : pattern.maxDistance;

    const { row, col } = piece.position;

    for (let distance = 1; distance <= maxDistance; distance++) {
      const targetPos: Position = {
        row: row + direction.rowDelta * distance,
        col: col + direction.colDelta * distance,
      };

      if (!isValidPosition(targetPos, board)) break;

      // Check if path is clear for sliding moves
      if (pattern.type === "slide" && distance > 1) {
        if (!isPathClear(board, piece.position, targetPos)) break;
      }

      const targetPiece = getPieceAt(board, targetPos);

      if (targetPiece) {
        // Can capture opponent piece
        if (targetPiece.color !== piece.color) {
          moves.push(targetPos);
        }
        // Stop after hitting any piece (for sliding moves)
        if (pattern.type === "slide") break;
      } else {
        // Empty square
        moves.push(targetPos);
      }
    }

    return moves;
  }

  /**
   * Check if a king is in check (INTERNAL - uses Board structure)
   */
  private isKingInCheckInternal(board: Board, color: PieceColor): boolean {
    if (!board || !board.dimensions || !board.squares) {
      console.error('isKingInCheckInternal: Invalid board structure');
      return false;
    }
    
    const king = findKing(board, color);
    if (!king) {
      // No king found - not in check (though this is unusual)
      return false;
    }

    return this.isPositionUnderAttack(king.position, color, board);
  }

  /**
   * Check if a position is under attack
   */
  private isPositionUnderAttack(
    position: Position,
    defenderColor: PieceColor,
    board: Board
  ): boolean {
    for (let row = 0; row < board.dimensions.rows; row++) {
      for (let col = 0; col < board.dimensions.cols; col++) {
        const piece = board.squares[row][col];
        if (piece && piece.color !== defenderColor) {
          // Don't include castling moves when checking for attacks (prevents infinite recursion)
          const moves = this.getPseudoLegalMoves(piece, board, false);
          if (moves.some((pos) => positionsEqual(pos, position))) {
            return true;
          }
        }
      }
    }
    return false;
  }

  /**
   * Apply a move to the board (mutates board)
   */
  private applyMove(board: Board, from: Position, to: Position): void {
    const piece = getPieceAt(board, from);
    if (!piece) return;

    setPieceAt(board, from, null);
    setPieceAt(board, to, { ...piece, position: to, hasMoved: true });
  }

  /**
   * Validate if a move is legal
   * @param diagonalCaptureOnly - If true, Knave/Squire can only capture diagonally
   */
  validateMove(
    piece: Piece,
    from: Position,
    to: Position,
    board: Board,
    diagonalCaptureOnly: boolean = false
  ): MoveValidationResult {
    const legalMoves = this.getLegalMoves(piece, board, diagonalCaptureOnly);
    const isValid = legalMoves.some((pos) => positionsEqual(pos, to));

    if (!isValid) {
      return { isValid: false, reason: "Illegal move" };
    }

    const capturedPiece = getPieceAt(board, to);

    // Check if this is a castling move
    if (this.isCastlingMove(piece, from, to, board)) {
      const rookPositions = this.getCastlingRookPositions(from, to, board);
      if (rookPositions) {
        return {
          isValid: true,
          isCastling: true,
          castlingRookFrom: rookPositions.rookFrom,
          castlingRookTo: rookPositions.rookTo,
        };
      }
    }

    return {
      isValid: true,
      capturedPiece: capturedPiece || undefined,
    };
  }

  /**
   * Check if the game is in checkmate
   */
  isCheckmate(board: Board, color: PieceColor): boolean {
    if (!this.isKingInCheckInternal(board, color)) {
      return false;
    }

    // Check if any piece can make a legal move
    for (let row = 0; row < board.dimensions.rows; row++) {
      for (let col = 0; col < board.dimensions.cols; col++) {
        const piece = board.squares[row][col];
        if (piece && piece.color === color) {
          const legalMoves = this.getLegalMoves(piece, board);
          if (legalMoves.length > 0) {
            return false; // Found at least one legal move
          }
        }
      }
    }

    return true; // No legal moves available
  }

  /**
   * Check if the game is in stalemate
   */
  isStalemate(board: Board, color: PieceColor): boolean {
    if (this.isKingInCheckInternal(board, color)) {
      return false; // Not stalemate if in check
    }

    // Check if any piece can make a legal move
    for (let row = 0; row < board.dimensions.rows; row++) {
      for (let col = 0; col < board.dimensions.cols; col++) {
        const piece = board.squares[row][col];
        if (piece && piece.color === color) {
          const legalMoves = this.getLegalMoves(piece, board);
          if (legalMoves.length > 0) {
            return false; // Found at least one legal move
          }
        }
      }
    }

    return true; // No legal moves available and not in check
  }
}
